/**
 * Zustand store for dataset creation wizard state management
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type {
  CompressionCodec,
  DatasetFormat,
  DatasetType,
  PartitionField,
  MetadataField,
} from '../types/dataset.types';

interface DatasetCreationState {
  // Step 1: Basic Info
  name: string;
  description: string;
  format: DatasetFormat;
  type: DatasetType;

  // Step 2: Configuration
  compression: CompressionCodec;
  schemaId: string | null;
  sourceId: string | null;

  // Step 3: Partitions (REQUIRED - at least 1)
  partitions: PartitionField[];

  // Step 4: Metadata (OPTIONAL)
  metadata: MetadataField[];

  // Wizard state
  currentStep: number;

  // Actions
  setName: (name: string) => void;
  setDescription: (description: string) => void;
  setFormat: (format: DatasetFormat) => void;
  setType: (type: DatasetType) => void;
  setCompression: (compression: CompressionCodec) => void;
  setSchemaId: (schemaId: string | null) => void;
  setSourceId: (sourceId: string | null) => void;
  addPartition: (name: string) => void;
  removePartition: (id: string) => void;
  updatePartition: (id: string, name: string) => void;
  addMetadata: (key: string, value: string) => void;
  removeMetadata: (id: string) => void;
  updateMetadata: (id: string, key: string, value: string) => void;
  nextStep: () => void;
  previousStep: () => void;
  setStep: (step: number) => void;
  reset: () => void;
  getCreationData: () => {
    name: string;
    description: string;
    format: DatasetFormat;
    type: DatasetType;
    compression: CompressionCodec;
    schemaId: string | null;
    sourceId: string | null;
    partitions: string[];
    metadata: Record<string, any>;
  };
}

const initialState = {
  name: '',
  description: '',
  format: 'parquet' as DatasetFormat,
  type: 'table' as DatasetType,
  compression: 'uncompressed' as CompressionCodec,
  schemaId: null,
  sourceId: null,
  partitions: [],
  metadata: [],
  currentStep: 1,
};

export const useDatasetCreationStore = create<DatasetCreationState>()(
  immer((set, get) => ({
    ...initialState,

    setName: (name) =>
      set((state) => {
        state.name = name;
      }),

    setDescription: (description) =>
      set((state) => {
        state.description = description;
      }),

    setFormat: (format) =>
      set((state) => {
        state.format = format;
      }),

    setType: (type) =>
      set((state) => {
        state.type = type;
      }),

    setCompression: (compression) =>
      set((state) => {
        state.compression = compression;
      }),

    setSchemaId: (schemaId) =>
      set((state) => {
        state.schemaId = schemaId;
      }),

    setSourceId: (sourceId) =>
      set((state) => {
        state.sourceId = sourceId;
      }),

    addPartition: (name) =>
      set((state) => {
        state.partitions.push({
          id: `partition-${Date.now()}-${Math.random()}`, // nosemgrep: rules.lgpl.nodejs_scan.javascript-crypto-rule-node_insecure_random_generator  -- opaque client-side list id, not a security value
          name,
        });
      }),

    removePartition: (id) =>
      set((state) => {
        state.partitions = state.partitions.filter((p) => p.id !== id);
      }),

    updatePartition: (id, name) =>
      set((state) => {
        const partition = state.partitions.find((p) => p.id === id);
        if (partition) {
          partition.name = name;
        }
      }),

    addMetadata: (key, value) =>
      set((state) => {
        state.metadata.push({
          id: `metadata-${Date.now()}-${Math.random()}`, // nosemgrep: rules.lgpl.nodejs_scan.javascript-crypto-rule-node_insecure_random_generator  -- opaque client-side list id, not a security value
          key,
          value,
        });
      }),

    removeMetadata: (id) =>
      set((state) => {
        state.metadata = state.metadata.filter((m) => m.id !== id);
      }),

    updateMetadata: (id, key, value) =>
      set((state) => {
        const meta = state.metadata.find((m) => m.id === id);
        if (meta) {
          meta.key = key;
          meta.value = value;
        }
      }),

    nextStep: () =>
      set((state) => {
        if (state.currentStep < 5) {
          state.currentStep += 1;
        }
      }),

    previousStep: () =>
      set((state) => {
        if (state.currentStep > 1) {
          state.currentStep -= 1;
        }
      }),

    setStep: (step) =>
      set((state) => {
        if (step >= 1 && step <= 5) {
          state.currentStep = step;
        }
      }),

    reset: () => set(() => ({ ...initialState })),

    getCreationData: () => {
      const state = get();
      return {
        name: state.name,
        description: state.description,
        format: state.format,
        type: state.type,
        compression: state.compression,
        schemaId: state.schemaId,
        sourceId: state.sourceId,
        partitions: state.partitions.map((p) => p.name),
        metadata: state.metadata.reduce((acc, m) => {
          acc[m.key] = m.value;
          return acc;
        }, {} as Record<string, any>),
      };
    },
  }))
);

// Selector hooks - select entire state to avoid re-render issues
export const useDatasetBasicInfo = () => {
  const name = useDatasetCreationStore((state) => state.name);
  const description = useDatasetCreationStore((state) => state.description);
  const format = useDatasetCreationStore((state) => state.format);
  const type = useDatasetCreationStore((state) => state.type);
  const setName = useDatasetCreationStore((state) => state.setName);
  const setDescription = useDatasetCreationStore((state) => state.setDescription);
  const setFormat = useDatasetCreationStore((state) => state.setFormat);
  const setType = useDatasetCreationStore((state) => state.setType);

  return {
    name,
    description,
    format,
    type,
    setName,
    setDescription,
    setFormat,
    setType,
  };
};

export const useDatasetConfiguration = () => {
  const compression = useDatasetCreationStore((state) => state.compression);
  const schemaId = useDatasetCreationStore((state) => state.schemaId);
  const sourceId = useDatasetCreationStore((state) => state.sourceId);
  const setCompression = useDatasetCreationStore((state) => state.setCompression);
  const setSchemaId = useDatasetCreationStore((state) => state.setSchemaId);
  const setSourceId = useDatasetCreationStore((state) => state.setSourceId);

  return {
    compression,
    schemaId,
    sourceId,
    setCompression,
    setSchemaId,
    setSourceId,
  };
};

export const useDatasetPartitions = () => {
  const partitions = useDatasetCreationStore((state) => state.partitions);
  const addPartition = useDatasetCreationStore((state) => state.addPartition);
  const removePartition = useDatasetCreationStore((state) => state.removePartition);
  const updatePartition = useDatasetCreationStore((state) => state.updatePartition);

  return {
    partitions,
    addPartition,
    removePartition,
    updatePartition,
  };
};

export const useDatasetMetadata = () => {
  const metadata = useDatasetCreationStore((state) => state.metadata);
  const addMetadata = useDatasetCreationStore((state) => state.addMetadata);
  const removeMetadata = useDatasetCreationStore((state) => state.removeMetadata);
  const updateMetadata = useDatasetCreationStore((state) => state.updateMetadata);

  return {
    metadata,
    addMetadata,
    removeMetadata,
    updateMetadata,
  };
};

export const useDatasetWizardNav = () => {
  const currentStep = useDatasetCreationStore((state) => state.currentStep);
  const nextStep = useDatasetCreationStore((state) => state.nextStep);
  const previousStep = useDatasetCreationStore((state) => state.previousStep);
  const setStep = useDatasetCreationStore((state) => state.setStep);

  return {
    currentStep,
    nextStep,
    previousStep,
    setStep,
  };
};
